import { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import AdminPortalSidebar from "./AdminPortalSidebar";
import { endCurrentSession } from "@/lib/authNavigation";
import { toast } from "sonner";
import { usePortalRole } from "@/hooks/usePortalRole";
import { Button } from "@/components/ui/button";
import { ChevronLeft, LogOut } from "lucide-react";
import PortalNotificationsPanel from "@/components/portal/PortalNotificationsPanel";
import EvoHomeFamilySwitcher from "@/components/family/EvoHomeFamilySwitcher";
import RoleSwitcher from "@/components/portal/RoleSwitcher";
import CommandPalette from "@/components/portal/CommandPalette";
import PortalContent from "@/components/portal/PortalContent";
import StaffMfaBanner from "@/components/security/StaffMfaBanner";
import ViewAsBanner from "@/components/portal/ViewAsBanner";
import { useIsEmbeddedAdminShell } from "./EmbeddedAdminShellContext";

interface AdminPortalShellProps {
  children: ReactNode;
  title?: string;
  backHref?: string;
  backLabel?: string;
}

const roleBadgeColors: Record<string, string> = {
  admin: "bg-destructive/10 text-destructive",
  consultant: "bg-primary/10 text-primary",
  installer: "bg-accent/10 text-accent",
  customer: "bg-green-500/10 text-green-700",
  partner: "bg-blue-500/10 text-blue-700",
  marketing_manager: "bg-purple-500/10 text-purple-700",
  introducer: "bg-orange-500/10 text-orange-700",
  sales_manager: "bg-primary/10 text-primary",
};

export default function AdminPortalShell({
  children,
  title,
  backHref,
  backLabel = "Back",
}: AdminPortalShellProps) {
  const { role, roles } = usePortalRole();
  const navigate = useNavigate();
  const embedded = useIsEmbeddedAdminShell();

  const handleSignOut = async () => {
    const outcome = await endCurrentSession();
    if (!outcome.ok) { toast.error("Sign-out did not complete", { description: outcome.message }); return; }
    navigate("/", { replace: true });
  };

  if (embedded) {
    // Rendered inside another AdminPortalShell (e.g. AdminPipeline tabs).
    // Skip the outer chrome to avoid nested SidebarProviders, but keep
    // the page title/back affordance so users still get context.
    return (
      <div className="w-full">
        {(title || backHref) && (
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              {backHref && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-2 mb-1 h-auto min-h-[44px] gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                  asChild
                >
                  <Link to={backHref}>
                    <ChevronLeft className="w-4 h-4" />
                    {backLabel}
                  </Link>
                </Button>
              )}
              {title && <h2 className="text-base font-semibold text-foreground md:text-lg">{title}</h2>}
            </div>
          </div>
        )}
        {children}
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-muted/30">
        <AdminPortalSidebar />

        <div className="flex-1 flex flex-col min-w-0">
          {/* Header — compact, safe-area aware for installed PWA mode */}
          <header className="sticky top-0 z-50 flex items-center justify-between px-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pt-[env(safe-area-inset-top)]">
            <div className="flex h-12 min-w-0 items-center gap-1 sm:gap-2">
              <SidebarTrigger className="h-10 w-10 min-h-[44px] min-w-[44px] touch-manipulation" />
              <RoleSwitcher />
              {role && roles.length < 2 && (
                <span className={`hidden sm:inline text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${roleBadgeColors[role] ?? ""}`}>
                  {role}
                </span>
              )}
            </div>

            <div className="flex h-12 shrink-0 items-center gap-0.5 sm:gap-2">
              <EvoHomeFamilySwitcher compact />
              <CommandPalette />
              <PortalNotificationsPanel />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="gap-1.5 text-muted-foreground hover:text-foreground h-10 min-h-[44px] text-xs touch-manipulation"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </header>

          <StaffMfaBanner />
          <ViewAsBanner />

          {/* Content */}
          <main className="flex-1 w-full p-4 md:p-6">
            <PortalContent width="default" className="px-0">
              {(title || backHref) && (
                <div className="mb-5 md:mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    {backHref && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-2 mb-1 h-auto min-h-[44px] gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                        asChild
                      >
                        <Link to={backHref}>
                          <ChevronLeft className="w-4 h-4" />
                          {backLabel}
                        </Link>
                      </Button>
                    )}
                    {title && <h1 className="text-lg font-bold text-foreground md:text-2xl">{title}</h1>}
                  </div>
                </div>
              )}
              {children}
            </PortalContent>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
